import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Lock, Eye, EyeOff, Save, X } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import Button from '../components/Button'
import { useAuth } from '../hooks/useAuth'
import { apiService } from '../services/api'
import { config } from '../config'

interface UserProfile {
  id: string
  email: string
  first_name?: string
  last_name?: string
  company_name?: string
  role?: string
  team?: string
  product_value?: string
  industry?: string
  business_focus?: string
  customer_segments?: string[]
  customer_locations?: string[]
  onboarding_completed?: boolean
}

export default function Profile() {
  const { user: authUser, logout } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [toast, setToast] = useState<{ id: number; message: string; type: 'success' | 'error' } | null>(null)
  
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    role: '',
    team: '',
    product_value: '',
    industry: '',
    business_focus: '',
    customer_segments: [] as string[],
    customer_locations: [] as string[]
  })
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})

  // Load user profile
  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const response = await apiService.users.getProfile()
      if (response.data.success && response.data.user) {
        const userData = response.data.user
        setProfile(userData)
        setProfileForm({
          company_name: userData.company_name || '',
          role: userData.role || '',
          team: userData.team || '',
          product_value: userData.product_value || '',
          industry: userData.industry || '',
          business_focus: userData.business_focus || '',
          customer_segments: userData.customer_segments || [],
          customer_locations: userData.customer_locations || []
        })
      }
    } catch (error: any) {
      console.error('[Profile] Error loading profile:', error)
      setToast({
        id: Date.now(),
        message: 'Failed to load profile. Please try again.',
        type: 'error'
      })
      setTimeout(() => setToast(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  const handleProfileChange = (field: string, value: any) => {
    setProfileForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCheckboxChange = (field: 'customer_segments' | 'customer_locations', value: string, checked: boolean) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }))
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      const response = await apiService.users.updateProfile(profileForm)
      if (response.data.success) {
        setToast({
          id: Date.now(),
          message: 'Profile updated successfully',
          type: 'success'
        })
        setTimeout(() => setToast(null), 5000)
        // Reload profile to get updated data
        await loadProfile()
      }
    } catch (error: any) {
      console.error('[Profile] Error updating profile:', error)
      setToast({
        id: Date.now(),
        message: error.response?.data?.error || 'Failed to update profile. Please try again.',
        type: 'error'
      })
      setTimeout(() => setToast(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  const validatePassword = () => {
    const errors: Record<string, string> = {}
    
    if (!passwordForm.current_password) {
      errors.current_password = 'Current password is required'
    }
    if (!passwordForm.new_password) {
      errors.new_password = 'New password is required'
    } else {
      if (passwordForm.new_password.length < 8) {
        errors.new_password = 'Password must be at least 8 characters'
      } else if (!/[a-z]/.test(passwordForm.new_password)) {
        errors.new_password = 'Password must include at least one lowercase letter'
      } else if (!/[A-Z]/.test(passwordForm.new_password)) {
        errors.new_password = 'Password must include at least one uppercase letter'
      } else if (!/\d/.test(passwordForm.new_password)) {
        errors.new_password = 'Password must include at least one number'
      } else if (!/[^A-Za-z0-9]/.test(passwordForm.new_password)) {
        errors.new_password = 'Password must include at least one special character'
      }
    }
    if (!passwordForm.confirm_password) {
      errors.confirm_password = 'Please confirm your new password'
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      errors.confirm_password = 'Passwords do not match'
    }
    
    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleChangePassword = async () => {
    if (!validatePassword()) {
      return
    }

    setSavingPassword(true)
    try {
      const response = await apiService.users.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      })
      if (response.data.success) {
        setToast({
          id: Date.now(),
          message: 'Password changed successfully',
          type: 'success'
        })
        setTimeout(() => setToast(null), 5000)
        // Reset password form
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        })
        setPasswordErrors({})
      }
    } catch (error: any) {
      console.error('[Profile] Error changing password:', error)
      const errorMessage = error.response?.data?.error || 'Failed to change password. Please try again.'
      setPasswordErrors({ general: errorMessage })
      setToast({
        id: Date.now(),
        message: errorMessage,
        type: 'error'
      })
      setTimeout(() => setToast(null), 5000)
    } finally {
      setSavingPassword(false)
    }
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tandem-blue"></div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Failed to load profile</p>
          <Button onClick={loadProfile}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      {toast && (
        <div className={`fixed top-4 right-4 text-sm px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 ${
          toast.type === 'error'
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-green-50 border border-green-200 text-green-800'
        }`}>
          {toast.type === 'error' ? (
            <X className="w-5 h-5 text-red-600" />
          ) : (
            <Save className="w-5 h-5 text-green-600" />
          )}
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="shrink-0">
        <PageHeader title="Profile" />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          {/* Account Information Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="space-y-4">
              {/* Email - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              {/* First Name - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={profile.first_name || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">First name cannot be changed</p>
              </div>

              {/* Last Name - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={profile.last_name || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Last name cannot be changed</p>
              </div>

              {/* Company Name - Editable */}
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  id="company_name"
                  type="text"
                  value={profileForm.company_name}
                  onChange={(e) => handleProfileChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                  placeholder="Enter your company name"
                />
              </div>
            </div>
          </div>

          {/* Onboarding Questions Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Onboarding Questions</h2>
            <div className="space-y-4">
              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Role
                </label>
                <input
                  id="role"
                  type="text"
                  value={profileForm.role}
                  onChange={(e) => handleProfileChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                  placeholder="e.g., Product Manager, Account Executive, etc."
                />
              </div>

              {/* Team */}
              <div>
                <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Team
                </label>
                <select
                  id="team"
                  value={profileForm.team}
                  onChange={(e) => handleProfileChange('team', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                >
                  <option value="">Select your team...</option>
                  <option value="sales">Sales</option>
                  <option value="customer_success">Customer Success</option>
                  <option value="product_management">Product Management</option>
                  <option value="program_management">Program Management</option>
                  <option value="product_marketing">Product Marketing</option>
                  <option value="operations">Operations</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Product Value Proposition */}
              <div>
                <label htmlFor="product_value" className="block text-sm font-medium text-gray-700 mb-1">
                  Product Value Propositions (2-3 sentences)
                </label>
                <textarea
                  id="product_value"
                  rows={3}
                  value={profileForm.product_value}
                  onChange={(e) => handleProfileChange('product_value', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                  placeholder="e.g., Tandem helps B2B teams discover and act on revenue opportunities faster."
                />
              </div>

              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <select
                  id="industry"
                  value={profileForm.industry}
                  onChange={(e) => handleProfileChange('industry', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                >
                  <option value="">Select your industry...</option>
                  <option value="Technology/Software">Technology/Software</option>
                  <option value="SaaS/Cloud Services">SaaS/Cloud Services</option>
                  <option value="Financial Technology">Financial Technology</option>
                  <option value="Healthcare/Medical">Healthcare/Medical</option>
                  <option value="E-commerce/Retail">E-commerce/Retail</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Consulting/Services">Consulting/Services</option>
                  <option value="Education/EdTech">Education/EdTech</option>
                  <option value="Media/Entertainment">Media/Entertainment</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Business Focus */}
              <div>
                <label htmlFor="business_focus" className="block text-sm font-medium text-gray-700 mb-1">
                  Business Focus Areas
                </label>
                <input
                  id="business_focus"
                  type="text"
                  value={profileForm.business_focus}
                  onChange={(e) => handleProfileChange('business_focus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                  placeholder="e.g., Revenue Operations, Customer Retention, Product Launches"
                />
              </div>

              {/* Customer Segments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Customer Segments
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['Startup', 'SMB', 'Enterprise', 'Nonprofit', 'Government', 'Other'].map((segment) => (
                    <label key={segment} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profileForm.customer_segments.includes(segment)}
                        onChange={(e) => handleCheckboxChange('customer_segments', segment, e.target.checked)}
                        className="h-4 w-4 text-tandem-blue focus:ring-tandem-blue border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{segment}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Customer Locations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Customer Locations
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['North America', 'Europe', 'Asia-Pacific', 'Middle East', 'Latin America', 'Africa', 'Global'].map((location) => (
                    <label key={location} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profileForm.customer_locations.includes(location)}
                        onChange={(e) => handleCheckboxChange('customer_locations', location, e.target.checked)}
                        className="h-4 w-4 text-tandem-blue focus:ring-tandem-blue border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{location}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Save Profile Button */}
              <div className="pt-4 border-t border-gray-200">
                <Button
                  variant="primary"
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </h2>
            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    id="current_password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30 ${
                      passwordErrors.current_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.current_password && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.current_password}</p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="new_password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => {
                      setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))
                      if (passwordErrors.new_password) {
                        setPasswordErrors(prev => ({ ...prev, new_password: '' }))
                      }
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30 ${
                      passwordErrors.new_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.new_password && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.new_password}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirm_password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => {
                      setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))
                      if (passwordErrors.confirm_password) {
                        setPasswordErrors(prev => ({ ...prev, confirm_password: '' }))
                      }
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30 ${
                      passwordErrors.confirm_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.confirm_password}</p>
                )}
              </div>

              {passwordErrors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{passwordErrors.general}</p>
                </div>
              )}

              {/* Change Password Button */}
              <div className="pt-4 border-t border-gray-200">
                <Button
                  variant="primary"
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="flex items-center gap-2"
                >
                  {savingPassword ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}



